/**
 * `useTriggers` — evaluate proactive-message triggers client-side (ARCHITECTURE §7, parity with
 * legacy Triggers). When a rule matches, `onFire(message)` is called exactly once (first match
 * wins) so the widget can auto-open with a proactive greeting.
 *
 * Conditions:
 *  - `time_on_page` — fires after N seconds on the page (a timer).
 *  - `url_contains` — fires when the host-page URL contains a substring. Inside the iframe the
 *    host URL isn't directly readable, so we use `document.referrer` (the embedding page), the
 *    same source the visitor-context capture uses for the page URL.
 *
 * `active` gates evaluation: pass `true` only while the visitor hasn't opened the widget or
 * started a session, so a proactive nudge never interrupts an in-progress chat. Firing is
 * one-shot for the widget's lifetime (a manual open/close never re-triggers it).
 */

import { useEffect, useRef } from 'react';
import type { PublicTrigger } from '@flozic/shared/types';

/** The embedding page's URL: prefer the loader-forwarded `hostUrl`, else document.referrer. */
function resolveHostUrl(hostUrl: string | null | undefined): string {
  if (hostUrl) return hostUrl;
  try {
    return document.referrer || '';
  } catch {
    return '';
  }
}

export function useTriggers(
  triggers: PublicTrigger[] | undefined,
  opts: { active: boolean; hostUrl?: string | null; onFire: (message: string) => void },
): void {
  const { active, hostUrl, onFire } = opts;
  const firedRef = useRef(false);
  const onFireRef = useRef(onFire);
  onFireRef.current = onFire;

  useEffect(() => {
    if (!active || !triggers?.length) return;
    const url = resolveHostUrl(hostUrl);
    const timers: ReturnType<typeof setTimeout>[] = [];

    const fire = (message: string): void => {
      if (firedRef.current) return;
      firedRef.current = true;
      onFireRef.current(message);
    };

    for (const t of triggers) {
      if (!t.enabled || !t.message) continue;
      const c = t.condition;
      if (c.type === 'url_contains') {
        if (c.urlContains && url.includes(c.urlContains)) {
          // Matches already — fire on the next tick so the panel can mount cleanly.
          timers.push(setTimeout(() => fire(t.message), 0));
        }
      } else if (c.type === 'time_on_page') {
        const secs = Math.max(0, c.seconds ?? 0);
        timers.push(setTimeout(() => fire(t.message), secs * 1000));
      }
    }

    return () => {
      for (const id of timers) clearTimeout(id);
    };
  }, [triggers, active, hostUrl]);
}
